import { createPopper } from "@popperjs/core";
import { ref, onUnmounted, nextTick } from "vue";

export const POPPER_PLACEMENT = {
    right: "right",
    left: "left",
    top: "top",
    bottom: "bottom"
};

export function usePopper() {
    // Reference to the popper itself (DOM element)
    // In mixin version: this.$el
    const popper_popperReference = ref(null);

    // Placement can be overwritten in the inherited component
    const popper_popperPlacement = ref(POPPER_PLACEMENT.right);

    // Modifiers can be overwritten in the inherited component
    let popper_modifiers = [];

    // Wrap the parent component in a `v-if/v-show="popper_isVisible"`
    // Note: If the popper isn't appearing with v-if, use v-show
    const popper_isVisible = ref(false);

    // The object/element the popper is appearing on
    let popper_referenceObject = null;

    const popper_useHideOnOutsideClick = ref(true);
    let popper_object = null;

    onUnmounted(() => {
        popper_hide();
    });

    function popper_setModifiers(modifiers) {
        popper_modifiers = modifiers;
    }

    function popper_hideOnOutsideClick(event) {
        if (
            event.target !== popper_referenceObject &&
            !popper_referenceObject.contains(event.target) &&
            event.target !== popper_popperReference.value &&
            !popper_popperReference.value.contains(event.target)
        ) {
            popper_hide();
        }
    }

    function popper_toggle(reference) {
        if (popper_isVisible.value) {
            popper_hide();
        } else {
            popper_show(reference);
        }
    }

    function popper_hide() {
        if (!popper_object) {
            return;
        }

        popper_object.destroy();
        popper_object = null;
        popper_referenceObject = null;
        popper_isVisible.value = false;
        document.removeEventListener("click", popper_hideOnOutsideClick, true);
    }

    async function popper_show(elementRef) {
        if (popper_object) {
            return;
        }

        popper_isVisible.value = true;
        popper_referenceObject = elementRef;

        // Wait for the root dom element to exist before creating the popper
        if (!(popper_popperReference.value instanceof HTMLElement)) {
            await nextTick();
        }

        popper_object = createPopper(elementRef, popper_popperReference.value, {
            placement: popper_popperPlacement.value,
            popper_modifiers
        });

        if (popper_useHideOnOutsideClick.value) {
            document.addEventListener("click", popper_hideOnOutsideClick, true);
        }
    }

    function popper_arrowClass() {
        switch (popper_popperPlacement.value) {
            case POPPER_PLACEMENT.right:
                return "bs-popover-end";
            case POPPER_PLACEMENT.left:
                return "bs-popover-start";
            case POPPER_PLACEMENT.top:
                return "bs-popover-top";
            case POPPER_PLACEMENT.bottom:
                return "bs-popover-bottom";
        }
    }

    return {
        popper_popperReference,
        popper_popperPlacement,
        popper_modifiers,
        popper_isVisible,
        popper_referenceObject,
        popper_useHideOnOutsideClick,
        popper_object,
        popper_setModifiers,
        popper_toggle,
        popper_hide,
        popper_show,
        popper_arrowClass
    };
}
